import { ColumnsType } from 'antd/es/table';
import { get } from 'lodash';
import { isValidElement, ReactNode } from 'react';

// Columns with no data of their own: the row actions and the super-admin
// payslip verifier badge.
const NON_DATA_COLUMN_KEYS = ['actions', 'payslipVerification'];

const EMPTY_PLACEHOLDERS = ['_', 'Not Set'];

/** The plain text a cell (or a column title) shows on screen. */
const textOf = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) return textOf((node.props as any)?.children);
  return '';
};

/** What the table renders for this column and row, as text. */
const cellText = (column: any, record: unknown, index: number): string => {
  // Same lookup antd does: no dataIndex hands the whole record to render.
  const value =
    column.dataIndex == null ? record : get(record, column.dataIndex);
  const rendered = column.render ? column.render(value, record, index) : value;
  const text = textOf(rendered).trim();
  // The table's empty-cell placeholders ("_", and "Not Set" for a missing
  // payment model) export as blank cells.
  return EMPTY_PLACEHOLDERS.includes(text) ? '' : text;
};

/**
 * A CSV of exactly what a table shows: headers are the column titles and
 * values are what each cell displays, in the table's column order. Used by the
 * Draft Payroll and Payment Summary exports so each file always matches its
 * screen (NX-5791).
 *
 * Returns the headers and rows; the caller owns the filename and ExportToCsv.
 */
export const buildTableExport = <T,>(
  data: T[],
  columns: ColumnsType<T>,
): { headers: string[]; dataExport: Record<string, string>[] } => {
  const exported = columns.filter(
    (column) => !NON_DATA_COLUMN_KEYS.includes(String(column.key)),
  );
  return {
    headers: exported.map((column) => textOf(column.title as ReactNode)),
    // Keyed by position: several columns share a key (e.g. subcontractorRef).
    dataExport: data.map((record, index) =>
      Object.fromEntries(
        exported.map((column, i) => [`c${i}`, cellText(column, record, index)]),
      ),
    ),
  };
};
